/**
 * Канбан-доска.
 *
 * Ключевое требование §12.1 и §12.3: перетаскивание проверяет разрешённый
 * переход ДО отправки на сервер. Колонка, куда переход запрещён, гаснет
 * во время перетаскивания — пользователь не должен узнавать об отказе
 * после броска.
 *
 * Столбцы здесь не перетаскиваются: их порядок меняется только в настройках
 * доски (§9.5).
 *
 * Телефон (docs/08-mobile-roadmap.md, M3): один столбец на экран с прилипанием
 * при прокрутке, сверху — полоса вкладок столбцов. Карточку берут долгим
 * нажатием (dndSensors.ts). Пока карточка в руке, прилипание снято: у края
 * экрана доска едет сама (автопрокрутка dnd-kit), задержка над вкладкой
 * перелистывает к её столбцу, а отпустить карточку можно и на вкладку.
 */

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  type CollisionDetection,
  type KeyboardCoordinateGetter,
  MeasuringStrategy,
  pointerWithin,
  rectIntersection,
  type UniqueIdentifier,
  useDroppable,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useIsPhone } from "../../lib/media";
import { cn } from "../cn";
import { useDndSensors } from "../dndSensors";
import { IconButton } from "../IconButton";
import { Menu, type MenuAction } from "../Menu";
import {
  evaluateMove,
  type KanbanCardData,
  type KanbanColumnData,
  type MoveIntent,
  resolveColumnId,
} from "@taskflow/client-core/boards";
import { TaskCard } from "./TaskCard";
import { MoreHorizontal, Plus } from "../motionIcons";

/** Вкладки столбцов на телефоне — тоже цели броска; id отличаются приставкой. */
const TAB_PREFIX = "tab:";
/** Сколько держать карточку над вкладкой, чтобы доска перелистнулась к столбцу. */
const TAB_DWELL_MS = 400;

interface KanbanBoardProps {
  columns: KanbanColumnData[];
  onMove: (intent: MoveIntent) => void;
  onOpenTask?: (key: string) => void;
  /** Без права перемещения карточки остаются кликабельными, но не таскаются. */
  canMove?: boolean;
  /** Причина запрета: показывается вместо молчаливого отказа. */
  onMoveRejected?: (reason: string) => void;
  /** «+» в заголовке столбца (§11.3): создание задачи с уже выбранным столбцом. */
  onCreateTask?: (columnId: string) => void;
  /** Пункты меню «⋯» столбца. Пустой список — меню не показывается. */
  columnActions?: (column: KanbanColumnData) => MenuAction[];
}

/**
 * Перемещение с клавиатуры между столбцами.
 *
 * sortableKeyboardCoordinates двигает карточку только внутри своего
 * SortableContext, а у каждого столбца он свой — то есть стрелками
 * между столбцами не попасть вообще. Карточку можно взять пробелом,
 * стрелки ничего не делают, отпускание молча возвращает её на место.
 * §19 требует, чтобы доступное мышью было доступно и с клавиатуры.
 *
 * Поэтому влево-вправо переносят в соседний столбец целиком, а
 * вверх-вниз оставлены встроенному расчёту для порядка внутри столбца.
 */
function makeCoordinateGetter(columnIds: string[]): KeyboardCoordinateGetter {
  return (event, args) => {
    if (event.code !== "ArrowLeft" && event.code !== "ArrowRight") {
      return sortableKeyboardCoordinates(event, args);
    }
    event.preventDefault();

    const { collisionRect, droppableRects, droppableContainers } = args.context;
    if (!collisionRect) return undefined;

    const ordered = columnIds
      .map((id) => {
        const container = droppableContainers.get(id);
        const rect = container ? droppableRects.get(container.id) : undefined;
        return rect ? { id, rect } : null;
      })
      .filter((item) => item !== null)
      .sort((a, b) => a.rect.left - b.rect.left);

    if (ordered.length === 0) return undefined;

    const centerX = collisionRect.left + collisionRect.width / 2;
    let current = ordered.findIndex(
      (item) => centerX >= item.rect.left && centerX <= item.rect.left + item.rect.width,
    );
    if (current === -1) current = 0;

    const from = ordered[current];
    const to = ordered[current + (event.code === "ArrowRight" ? 1 : -1)];
    // На краю доски остаёмся на месте: перескакивать с последнего
    // столбца на первый значило бы уносить задачу совсем не туда.
    if (!from || !to) return undefined;

    // Возвращается смещение, а не абсолютная точка: расчёт координат
    // работает в системе перетаскивания, а не страницы.
    return {
      x: args.currentCoordinates.x + (to.rect.left - from.rect.left),
      y: args.currentCoordinates.y,
    };
  };
}

export function KanbanBoard({
  columns,
  onMove,
  onOpenTask,
  canMove = true,
  onMoveRejected,
  onCreateTask,
  columnActions,
}: KanbanBoardProps) {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);

  const columnIds = useMemo(() => columns.map((column) => column.id), [columns]);
  const coordinateGetter = useMemo(() => makeCoordinateGetter(columnIds), [columnIds]);

  // Мышь, долгое нажатие пальцем и клавиатура — см. dndSensors.ts.
  const sensors = useDndSensors(coordinateGetter);

  const phone = useIsPhone();
  const scroller = useRef<HTMLDivElement>(null);
  const [visibleIndex, setVisibleIndex] = useState(0);

  /** Какой столбец сейчас на экране — по прокрутке ленты. */
  const trackVisible = () => {
    const node = scroller.current;
    const first = node?.firstElementChild as HTMLElement | null | undefined;
    if (!node || !first) return;
    const step = first.offsetWidth + parseFloat(getComputedStyle(node).columnGap || "0");
    setVisibleIndex(Math.round(node.scrollLeft / step));
  };

  const scrollToColumn = useCallback((columnId: string) => {
    const node = Array.from(scroller.current?.children ?? []).find(
      (child) => (child as HTMLElement).dataset.columnId === columnId,
    );
    node?.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
  }, []);

  const cardIndex = useMemo(() => {
    const map = new Map<string, { card: KanbanCardData; column: KanbanColumnData }>();
    for (const column of columns) {
      for (const card of column.cards) map.set(card.key, { card, column });
    }
    return map;
  }, [columns]);

  const active = activeKey === null ? null : (cardIndex.get(activeKey) ?? null);

  // Доска такой, какой она станет при отпускании: карточка уже стоит
  // в выбранном столбце, соседи раздвинуты. Прежде в чужом столбце места
  // не было видно вовсе, и бросок между карточками уходил в конец.
  const shown = useMemo(
    () => placeCard(columns, activeKey, placement),
    [columns, activeKey, placement],
  );

  // После переноса в другой столбец раскладка меняется, и на кадр цель
  // под указателем пропадает; без запоминания последней цели карточка
  // металась бы между столбцами.
  const lastOverId = useRef<UniqueIdentifier | null>(null);
  const movedToNewColumn = useRef(false);
  useEffect(() => {
    requestAnimationFrame(() => {
      movedToNewColumn.current = false;
    });
  }, [shown]);

  /**
   * Определение цели.
   *
   * pointerWithin точнее всего для мыши, но при перетаскивании с клавиатуры
   * указателя нет — тогда берётся пересечение прямоугольников (§19).
   *
   * Если под указателем столбец, а не карточка (заголовок, зазор между
   * карточками, пустой низ), целью становится ближайшая карточка этого
   * столбца. Прежде такой бросок означал «в конец»: наверх столбца
   * попасть было почти невозможно.
   */
  const collisionDetection = useCallback<CollisionDetection>(
    (args) => {
      const byPointer = pointerWithin(args);
      const isColumn = (id: UniqueIdentifier) => shown.some((column) => column.id === id);

      let overId: UniqueIdentifier | null =
        byPointer.length > 0
          ? (byPointer.find((hit) => !isColumn(hit.id)) ?? byPointer[0])!.id
          : (rectIntersection(args)[0]?.id ?? null);

      if (overId !== null && isColumn(overId)) {
        const keys = new Set(shown.find((column) => column.id === overId)?.cards.map((card) => card.key));
        if (keys.size > 0) {
          overId =
            closestCenter({
              ...args,
              droppableContainers: args.droppableContainers.filter((container) =>
                keys.has(String(container.id)),
              ),
            })[0]?.id ?? overId;
        }
      }

      if (overId !== null) {
        lastOverId.current = overId;
        return [{ id: overId }];
      }
      if (movedToNewColumn.current) lastOverId.current = activeKey;
      return lastOverId.current === null ? [] : [{ id: lastOverId.current }];
    },
    [shown, activeKey],
  );

  const reset = () => {
    setActiveKey(null);
    setPlacement(null);
    lastOverId.current = null;
  };

  /** Перенос в другой столбец во время перетаскивания — только показ места. */
  const handleDragOver = (event: DragOverEvent) => {
    const { active: dragged, over } = event;
    if (!over) return;
    const key = String(dragged.id);
    const source = cardIndex.get(key);
    const current = shown.find((column) => column.cards.some((card) => card.key === key));
    const overId = fromTab(String(over.id));
    const targetId = resolveColumnId(overId, shown);
    // Внутри столбца место показывает сама сортировка.
    if (!source || !current || targetId === null || targetId === current.id) return;

    // В столбец, куда переход запрещён, место не раскрывается: бросок туда
    // ничего не изменит, а причину покажет отпускание.
    if (
      targetId !== source.column.id &&
      !evaluateMove({ card: source.card, sourceColumn: source.column, targetColumnId: targetId, columns })
        .allowed
    ) {
      return;
    }

    const target = shown.find((column) => column.id === targetId);
    if (!target) return;
    let index = target.cards.length;
    if (overId !== targetId) {
      const overIndex = target.cards.findIndex((card) => card.key === overId);
      const y = pointerY(event);
      index = overIndex + (y !== null && y > over.rect.top + over.rect.height / 2 ? 1 : 0);
    }

    movedToNewColumn.current = true;
    setPlacement({ columnId: targetId, index });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const layout = shown;
    reset();
    const { active: dragged, over } = event;
    if (!over) return;

    const key = String(dragged.id);
    const source = cardIndex.get(key);
    const current = layout.find((column) => column.cards.some((card) => card.key === key));
    const overId = fromTab(String(over.id));
    const targetId = resolveColumnId(overId, layout);
    if (!source || !current || targetId === null) return;

    const report = (decision: ReturnType<typeof evaluateMove>) => {
      if (decision.allowed) onMove(decision.intent);
      else if (decision.reason !== null) onMoveRejected?.(decision.reason);
    };

    if (targetId !== current.id) {
      // Место не раскрылось — значит, переход запрещён; причину даёт evaluateMove.
      report(
        evaluateMove({ card: source.card, sourceColumn: source.column, targetColumnId: targetId, columns }),
      );
      return;
    }

    // Итоговый порядок — ровно тот, что был на экране в момент отпускания:
    // сортировка ставит карточку на место той, над которой она находится.
    const keys = current.cards.map((card) => card.key);
    const from = keys.indexOf(key);
    const to = overId === targetId ? from : keys.indexOf(overId);
    const order = arrayMove(keys, from, to);
    const position = order.indexOf(key);
    const others = order.filter((item) => item !== key);

    // Решение вынесено в чистую функцию — см. evaluateMove.ts. Место
    // передаётся через соседа: перед первой карточкой или после предыдущей.
    report(
      evaluateMove({
        card: source.card,
        sourceColumn: source.column,
        targetColumnId: targetId,
        columns,
        overCardKey: position === 0 ? (others[0] ?? null) : (others[position - 1] ?? null),
        placeAfter: position !== 0,
      }),
    );
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      // Раскладка меняется во время перетаскивания — размеры нужно
      // перемерять постоянно, а не только в начале.
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      // На телефоне столбец во всю ширину: при обычной скорости автопрокрутка
      // проскакивала два столбца за секунду удержания у края. Медленнее и
      // у самого края — точный выбор столбца даёт задержка над вкладкой.
      {...(phone ? { autoScroll: { acceleration: 4, threshold: { x: 0.12, y: 0.15 } } } : {})}
      onDragStart={(event: DragStartEvent) => {
        setActiveKey(String(event.active.id));
        // Отклик под пальцем: карточка взята. На iOS вибрации нет — там
        // остаётся видимый «подъём» карточки.
        if (event.activatorEvent.type === "touchstart") navigator.vibrate?.(15);
      }}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={reset}
      accessibility={{
        announcements: {
          onDragStart: ({ active: a }) => `Взята задача ${String(a.id)}`,
          onDragOver: ({ active: a, over }) =>
            over ? `Задача ${String(a.id)} над столбцом ${String(over.id)}` : "",
          onDragEnd: ({ active: a, over }) =>
            over ? `Задача ${String(a.id)} перемещена в ${String(over.id)}` : "Перемещение отменено",
          onDragCancel: ({ active: a }) => `Перемещение задачи ${String(a.id)} отменено`,
        },
      }}
    >
      <div className="flex h-full flex-col">
      {phone && (
        <ColumnTabs
          columns={shown}
          visibleIndex={visibleIndex}
          dragging={activeKey !== null}
          onSelect={scrollToColumn}
        />
      )}
      <div
        ref={scroller}
        onScroll={phone ? trackVisible : undefined}
        className={cn(
          "flex min-h-0 flex-1 gap-3 overflow-x-auto p-4",
          // Во время переноса прилипание снято, иначе оно дёргало бы
          // автопрокрутку назад к началу столбца.
          phone && "px-4 py-3",
          phone && activeKey === null && "snap-x snap-mandatory",
        )}
      >
        {shown.map((column) => (
          <Column
            key={column.id}
            phone={phone}
            column={column}
            canMove={canMove}
            {...(onOpenTask === undefined ? {} : { onOpenTask })}
            {...(onCreateTask === undefined ? {} : { onCreateTask })}
            actions={columnActions?.(column) ?? []}
            allowedFromActive={active === null ? null : isTransitionAllowed(active.column, column.id)}
            isSource={active?.column.id === column.id}
          />
        ))}
      </div>
      </div>

      <DragOverlay dropAnimation={null}>
        {active && <TaskCard card={active.card} dragging />}
      </DragOverlay>
    </DndContext>
  );
}

/**
 * Вкладка столбца означает сам столбец — бросок на неё то же, что на
 * столбец. Целью dnd-kit вкладка остаётся, чтобы она сама знала, что над
 * ней карточка (подсветка и перелистывание к столбцу).
 */
function fromTab(id: string): string {
  return id.startsWith(TAB_PREFIX) ? id.slice(TAB_PREFIX.length) : id;
}

/** Куда встанет перетаскиваемая карточка, если её отпустить. */
interface Placement {
  columnId: string;
  index: number;
}

/** Столбцы с карточкой, уже поставленной на место предпросмотра. */
function placeCard(
  columns: KanbanColumnData[],
  key: string | null,
  placement: Placement | null,
): KanbanColumnData[] {
  if (key === null || placement === null) return columns;
  const card = columns.flatMap((column) => column.cards).find((item) => item.key === key);
  if (!card) return columns;
  return columns.map((column) => {
    const cards = column.cards.filter((item) => item.key !== key);
    if (column.id === placement.columnId) {
      cards.splice(Math.min(placement.index, cards.length), 0, card);
    }
    return cards.length === column.cards.length && column.id !== placement.columnId
      ? column
      : { ...column, cards };
  });
}

/** Вертикаль указателя; у перетаскивания с клавиатуры — центр карточки. */
function pointerY(event: DragOverEvent): number | null {
  const start = event.activatorEvent;
  if ("clientY" in start && typeof start.clientY === "number") return start.clientY + event.delta.y;
  const box = event.active.rect.current.translated;
  return box === null ? null : box.top + box.height / 2;
}

interface ColumnProps {
  column: KanbanColumnData;
  /** Телефон: столбец во всю ширину ленты и прилипает при прокрутке. */
  phone: boolean;
  canMove: boolean;
  onOpenTask?: (key: string) => void;
  /** null — перетаскивания нет; иначе разрешён ли переход в этот столбец. */
  allowedFromActive: boolean | null;
  isSource: boolean;
  onCreateTask?: (columnId: string) => void;
  actions: MenuAction[];
}

/** §12.3 — то же правило, что в evaluateMove и на сервере: пустой граф
 *  разрешает любой переход. Подсветка обязана совпадать с тем, что
 *  произойдёт при отпускании, иначе она вводит в заблуждение. */
function isTransitionAllowed(source: KanbanColumnData, targetId: string): boolean {
  return source.allowedTargets.length === 0 || source.allowedTargets.includes(targetId);
}

function Column({
  column,
  phone,
  canMove,
  onOpenTask,
  allowedFromActive,
  isSource,
  onCreateTask,
  actions,
}: ColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });
  const forbidden = allowedFromActive === false && !isSource;

  return (
    <section
      ref={setNodeRef}
      aria-label={column.name}
      data-column-id={column.id}
      className={cn(
        // overflow-hidden: без него полоса прокрутки карточек рисовалась
        // поверх скруглённой рамки столбца.
        "flex shrink-0 flex-col overflow-hidden rounded-lg border bg-sunken transition-colors",
        phone ? "w-full snap-start scroll-mx-4" : "w-72",
        isOver && allowedFromActive === true ? "border-accent" : "border-line",
        forbidden && "opacity-40",
      )}
    >
      {/* Макет: «⋯» слева, название и число задач по центру, «+» справа.
          Прежде в заголовке были только название и число — ни создать
          задачу в столбце, ни переименовать его на доске было нельзя. */}
      <header className="grid grid-cols-[1.75rem_minmax(0,1fr)_1.75rem] items-center gap-1 px-1.5 py-2">
        <div>
          {actions.length > 0 && (
            <Menu
              align="start"
              trigger={
                <IconButton
                  icon={MoreHorizontal}
                  label={`Действия со столбцом ${column.name}`}
                  size="sm"
                  className="cursor-pointer"
                />
              }
              actions={actions}
            />
          )}
        </div>
        <div className="min-w-0 text-center">
          <div className="flex items-baseline justify-center gap-1.5">
            <h2 className="truncate text-[13px] font-extrabold text-fg" title={column.name}>
              {column.name}
            </h2>
            <span className="shrink-0 text-xs font-semibold text-faint tabular-nums">
              {column.cards.length}
            </span>
          </div>
          {column.projectName !== null && column.projectName !== undefined && (
            <p className="truncate text-[11px] text-muted">{column.projectName}</p>
          )}
        </div>
        <div>
          {onCreateTask !== undefined && (
            <IconButton
              icon={Plus}
              label={`Создать задачу в столбце ${column.name}`}
              title="Создать задачу в этом столбце"
              size="sm"
              className="cursor-pointer"
              onClick={() => onCreateTask(column.id)}
            />
          )}
        </div>
      </header>

      <SortableContext
        items={column.cards.map((card) => card.key)}
        strategy={verticalListSortingStrategy}
      >
        <div className="flex min-h-24 flex-1 flex-col gap-2 overflow-y-auto px-2 pb-2 [scrollbar-width:thin]">
          {column.cards.map((card) => (
            <SortableCard
              key={card.key}
              card={card}
              disabled={!canMove}
              {...(onOpenTask === undefined ? {} : { onOpen: onOpenTask })}
            />
          ))}
          {column.cards.length === 0 && (
            <p className="px-2 py-6 text-center text-xs text-faint">Пусто</p>
          )}
        </div>
      </SortableContext>
    </section>
  );
}

function SortableCard({
  card,
  disabled,
  onOpen,
}: {
  card: KanbanCardData;
  disabled: boolean;
  onOpen?: (key: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: card.key,
    disabled,
  });
  // Карточка открывается кликом по любому месту. Отпущенное над ней же
  // перетаскивание браузер тоже считает кликом — такой клик гасится.
  const dragged = useRef(false);
  useEffect(() => {
    if (isDragging) dragged.current = true;
  }, [isDragging]);

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      // Оригинал прячется, пока карточка едет в DragOverlay.
      // touch-manipulation: палец листает доску, пока не сработало долгое
      // нажатие; без выделения текста и системного меню iOS на удержание.
      className={cn(
        "touch-manipulation select-none [-webkit-touch-callout:none]",
        isDragging && "opacity-0",
      )}
      {...attributes}
      {...listeners}
      onClickCapture={(event) => {
        if (!dragged.current) return;
        dragged.current = false;
        event.stopPropagation();
      }}
      onPointerDown={(event) => {
        dragged.current = false;
        listeners?.onPointerDown?.(event);
      }}
    >
      <TaskCard card={card} {...(onOpen === undefined ? {} : { onOpen })} />
    </div>
  );
}

/**
 * Полоса вкладок столбцов на телефоне. Нажатие — прокрутка к столбцу;
 * во время переноса вкладка — цель броска, а задержка над ней
 * перелистывает доску к столбцу, чтобы было видно, куда встанет карточка.
 */
function ColumnTabs({
  columns,
  visibleIndex,
  dragging,
  onSelect,
}: {
  columns: KanbanColumnData[];
  visibleIndex: number;
  dragging: boolean;
  onSelect: (columnId: string) => void;
}) {
  const strip = useRef<HTMLDivElement>(null);

  // Вкладка видимого столбца не должна уходить за край полосы.
  useEffect(() => {
    strip.current?.children[visibleIndex]?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [visibleIndex]);

  return (
    <div
      ref={strip}
      role="tablist"
      aria-label="Столбцы доски"
      className="flex shrink-0 gap-1 overflow-x-auto border-b border-line bg-surface px-3 py-1.5 [scrollbar-width:none]"
    >
      {columns.map((column, index) => (
        <ColumnTab
          key={column.id}
          column={column}
          current={index === visibleIndex}
          dragging={dragging}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

function ColumnTab({
  column,
  current,
  dragging,
  onSelect,
}: {
  column: KanbanColumnData;
  current: boolean;
  dragging: boolean;
  onSelect: (columnId: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `${TAB_PREFIX}${column.id}` });

  useEffect(() => {
    if (!isOver || current) return;
    const timer = setTimeout(() => onSelect(column.id), TAB_DWELL_MS);
    return () => clearTimeout(timer);
  }, [isOver, current, column.id, onSelect]);

  return (
    <button
      ref={setNodeRef}
      type="button"
      role="tab"
      aria-selected={current}
      onClick={() => onSelect(column.id)}
      className={cn(
        "flex min-h-9 shrink-0 items-center gap-1.5 rounded-md border px-3 text-[13px] font-semibold whitespace-nowrap transition-colors",
        current ? "border-accent bg-accent-soft text-fg" : "border-transparent text-muted",
        dragging && isOver && "border-accent bg-accent-soft",
      )}
    >
      {column.name}
      <span className="text-xs text-faint tabular-nums">{column.cards.length}</span>
    </button>
  );
}
